import React, { useState, useMemo } from 'react';
import { Modal, Toast } from 'react-bootstrap';
import { Controller, useWatch } from 'react-hook-form';
import TextareaAutosize from "react-textarea-autosize";

const AddModuloModal = ({
	showModalAddModulo,
	setShowModalAddModulo,
	setShowModalAddAula,
	setReloadData,
	reloadData,
	moduloAddFile,
	setModuloAddFile,
	register_M,
	errors_M,
	control_M,
	handleSubmit_M,
	onSubmitAddModulo,
	handleKeyPress,
	idCurso: idCursoProp,
	dataCursos,
	setValue_M,
	setError_M,
	clearErrors_M
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [toast, setToast] = useState({});
	const [showToast, setShowToast] = useState(false);

	// Observar o idCurso selecionado no formulário
	const idCursoSelecionado = useWatch({
		control: control_M,
		name: 'idCurso'
	});

	// Verificar se o curso selecionado é do tipo misto/híbrido
	const isCursoMisto = useMemo(() => {
		const cursoId = idCursoSelecionado || idCursoProp;
		if (!cursoId || !dataCursos || dataCursos.length === 0) return false;
		const curso = dataCursos.find(c => c.id === parseInt(cursoId));
		return curso && curso.tipo === 'misto';
	}, [idCursoSelecionado, idCursoProp, dataCursos]);

	// Pré-seleção de curso removida daqui - agora é feita apenas em openAddModuloModal
	// para evitar re-renders duplicados

	const handleClose = () => {
		// Limpar erros ao fechar o modal
		if (clearErrors_M) {
			clearErrors_M();
		}
		setShowModalAddModulo(false);
		// Só abrir modal de aula se setShowModalAddAula estiver disponível E não tiver idCurso pré-selecionado (contexto do Dashboard)
		// Se tiver idCurso pré-selecionado, significa que estamos na página Modulos, então não abrir modal de aula
		if (setShowModalAddAula && !idCursoProp) {
			setShowModalAddAula(true);
		}
		if (setReloadData) {
			setReloadData(!reloadData);
		}
	};

	// Função para parsear mensagens de erro do backend e associar aos campos
	const parseBackendError = (message) => {
		if (!message || typeof message !== 'string') return null;

		// Mapeamento de campos do backend para campos do formulário
		const fieldMap = {
			'carga_horaria': 'carga_horaria',
			'carga_horaria_pratica': 'carga_horaria_pratica',
			'nome': 'nome',
			'resumo': 'resumo',
			'descricao': 'descricao',
			'thumbnail': 'thumbnail',
			'idCurso': 'idCurso'
		};

		// Procurar por padrões como "O campo 'nome_do_campo' é obrigatório"
		const patterns = [
			/campo\s+['"]([^'"]+)['"]/i,
			/campo\s+([a-z_]+)/i,
			/['"]([^'"]+)['"]\s+é\s+obrigatório/i
		];

		for (const pattern of patterns) {
			const match = message.match(pattern);
			if (match && match[1]) {
				const backendField = match[1].toLowerCase();
				const formField = fieldMap[backendField] || backendField;
				return { field: formField, message };
			}
		}

		// Se não encontrar padrão específico, retornar a mensagem completa
		return { field: null, message };
	};

	// Função para mostrar mensagens de toast
	const showToastMessage = (header, body, variant = 'error') => {
		const type = variant === 'success' ? 'toast-success' : variant === 'danger' ? 'toast-error' : 'toast-' + variant;
		setToast({ header, body, type });
		setShowToast(true);
	};

	const handleSubmitWithLoading = async (data) => {
		setIsLoading(true);
		try {
			const result = await onSubmitAddModulo(data);

			if (result && result.success) {
				// Sucesso: mostrar toast e fechar modal
				showToastMessage('Sucesso!', result.message || 'Módulo adicionado com sucesso.', 'success');

				// Fechar modal imediatamente e chamar callback
				setShowModalAddModulo(false);
				if (clearErrors_M) {
					clearErrors_M();
				}
				// Chamar callback de sucesso para recarregar lista de módulos
				if (result.onSuccess) {
					result.onSuccess();
				}
			} else if (result && !result.success && result.message && setError_M) {
				// Erro: parsear e mostrar nos campos
				const errorInfo = parseBackendError(result.message);

				if (errorInfo && errorInfo.field) {
					// Definir erro no campo específico
					setError_M(errorInfo.field, {
						type: 'manual',
						message: errorInfo.message
					});
				} else if (errorInfo && errorInfo.message) {
					// Se não conseguir identificar o campo, definir erro genérico no primeiro campo
					setError_M('nome', {
						type: 'manual',
						message: errorInfo.message
					});
				}
				// Mostrar toast de erro também
				showToastMessage('Erro!', result.message || 'Erro ao adicionar módulo.', 'danger');
			}
		} catch (error) {
			showToastMessage('Erro!', error.message || 'Ocorreu um erro inesperado.', 'danger');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			<Toast
				show={showToast}
				onClose={() => setShowToast(false)}
				delay={4000}
				autohide
				className={`position-fixed top-0 end-0 m-3 toast-icon ${toast.type || 'toast-error'}`}
				style={{ zIndex: 1060 }}
			>
				<Toast.Header>
					<strong className="me-auto">{toast.header}</strong>
				</Toast.Header>
				<Toast.Body>{toast.body}</Toast.Body>
			</Toast>

			<Modal
				size="lg"
				show={showModalAddModulo}
				onHide={handleClose}
				backdrop="static"
			>
				<Modal.Header closeButton>
					<span className="fw-semibold fs-5 text-primary">Adicionar Módulo</span>
				</Modal.Header>
				<form onSubmit={handleSubmit_M(handleSubmitWithLoading)}>
					<Modal.Body className="bg-gray-100 p-4">
						<div className="row">
							<div className="col-md-12">
								<div className="body">
									<div className="d-flex mb-5">
										<div className="d-flex flex-column w-100">
											{/* Campo idCurso oculto - sempre necessário */}
											<input
												type='hidden'
												{...register_M('idCurso', { required: 'Curso é obrigatório.' })}
											/>
											{errors_M.idCurso && (
												<span className='text-danger fs-7 d-block mt-1'>{errors_M.idCurso.message || 'Curso é obrigatório.'}</span>
											)}
											<div className="row mb-3">
												<div className="col-12 col-md-12">
													<div className="mb-3">
														<label className="form-label text-primary fw-medium">Titulo</label>
														<input
															type="text"
															{...register_M('nome', { required: true })}
															placeholder="Título do Módulo"
															className="form-control border-new-light"
														/>
														{errors_M.nome && (
															<span className='text-danger fs-7 d-block mt-1'>{errors_M.nome.message || 'Título do módulo é obrigatório.'}</span>
														)}
													</div>
												</div>
											</div>
											<div className="row mb-3">
												<div className="col-12 col-md-4">
													<div className="mb-3">
														<label className="form-label text-primary fw-medium">Thumbnail</label>
														<label
															htmlFor="thumbnail"
															className={`form-control label-input-simple ${moduloAddFile ? 'border-success bg-success' : 'border-primary bg-primary'} text-white fw-medium text-center`}
														>
															<i className="bi bi-card-image me-2"></i>Enviar Arquivo
														</label>
														<Controller
															control={control_M}
															name="thumbnail"
															rules={{ required: true }}
															render={({ field: { onChange, onBlur, value, ref } }) => (
																<input
																	id='thumbnail'
																	type="file"
																	onBlur={onBlur}
																	ref={ref}
																	onChange={(e) => {
																		const files = e.target.files;
																		onChange(files);
																		if (files && files.length > 0) {
																			setModuloAddFile(true);
																		} else {
																			setModuloAddFile(false);
																		}
																	}}
																/>
															)}
														/>
														{errors_M.thumbnail && (
															<span className='text-danger fs-7 d-block mt-1'>{errors_M.thumbnail.message || 'Imagem do módulo é obrigatória.'}</span>
														)}
													</div>
												</div>
												<div className="col-12 col-md-4">
													<div className="mb-3">
														<label className="form-label text-primary fw-medium">Carga Horária Total</label>
														<input
															type="text"
															maxLength="5"
															onKeyDown={handleKeyPress}
															{...register_M('carga_horaria', { required: 'Carga horária total é obrigatória.' })}
															placeholder="00:00"
															className="form-control border-new-light"
														/>
														{errors_M.carga_horaria && (
															<span className='text-danger fs-7 d-block mt-1'>{errors_M.carga_horaria.message || 'Carga horária total é obrigatória.'}</span>
														)}
													</div>
												</div>
												{isCursoMisto && (
													<div className="col-12 col-md-4">
														<div className="mb-3">
															<label className="form-label text-primary fw-medium">Carga Horária Prática</label>
															<input
																type="text"
																maxLength="5"
																onKeyDown={handleKeyPress}
																{...register_M('carga_horaria_pratica', { required: 'Carga horária prática é obrigatória.' })}
																placeholder="00:00"
																className="form-control border-new-light"
															/>
															{errors_M.carga_horaria_pratica && (
																<span className='text-danger fs-7 d-block mt-1'>{errors_M.carga_horaria_pratica.message || 'Carga horária prática é obrigatória.'}</span>
															)}
														</div>
													</div>
												)}
											</div>
											<div className="row mb-3">
												<div className="col-12 col-md-12">
													<div className="mb-3">
														<label className="form-label text-primary fw-medium">Resumo</label>
														<TextareaAutosize
															{...register_M('resumo', { required: true })}
															placeholder="Resumo"
															className="form-control border-new-light"
														/>
														{errors_M.resumo && (
															<span className='text-danger fs-7 d-block mt-1'>{errors_M.resumo.message || 'Resumo é obrigatório.'}</span>
														)}
													</div>
												</div>
											</div>
											<div className="row mb-3">
												<div className="col-12">
													<div className="mb-3">
														<label className="form-label text-primary fw-medium">Descrição</label>
														<TextareaAutosize
															{...register_M('descricao')}
															placeholder="Descrição sobre o módulo"
															className="form-control border-new-light"
														/>
														{errors_M.descricao && (
															<span className='text-danger fs-7 d-block mt-1'>{errors_M.descricao.message || 'Descrição é obrigatória.'}</span>
														)}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</Modal.Body>
					<Modal.Footer className="justify-content-end">
						<div className="d-flex justify-content-end">
							<button
								type="button"
								className="btn me-2"
								onClick={handleClose}
								disabled={isLoading}
							>
								Cancelar
							</button>
							<button type="submit" className="btn btn-primary" disabled={isLoading}>
								{isLoading ? (
									<>
										<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
										Salvando...
									</>
								) : (
									'Salvar'
								)}
							</button>
						</div>
					</Modal.Footer>
				</form>
			</Modal>
		</>
	);
};

export default AddModuloModal; 